'use client';

/**
 * RelationshipGraph - Force-directed network graph for entity relationships.
 *
 * Uses d3-force for physics simulation and React SVG for rendering.
 * Nodes represent individuals, entities, or cases; edges show relationships
 * such as ownership %, borrower role, or guarantor amount.
 *
 * @see Issue #1198 - Level 3 relationship visualization
 */

import { Box, Typography, useTheme } from '@mui/material';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import {
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { baseColors } from '../../theme/palette';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface GraphNode {
  id: string;
  type: 'individual' | 'entity' | 'case';
  label: string;
  href?: string;
}

export interface GraphEdge {
  source: string;
  target: string;
  label: string;
  type: 'ownership' | 'borrower' | 'guarantor';
}

export interface RelationshipGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  onNodeClick?: (node: GraphNode) => void;
  width?: number;
  height?: number;
  /** The central/root node ID to highlight */
  rootNodeId?: string;
}

// ---------------------------------------------------------------------------
// Internal simulation types
// ---------------------------------------------------------------------------

interface SimNode extends GraphNode, SimulationNodeDatum {
  x: number;
  y: number;
}

interface SimEdge extends SimulationLinkDatum<SimNode> {
  source: SimNode;
  target: SimNode;
  label: string;
  edgeType: GraphEdge['type'];
}

// ---------------------------------------------------------------------------
// Color helpers (use baseColors tokens, never hardcoded hex)
// ---------------------------------------------------------------------------

// TODO(#1758 F2): kept as blue (graph node/edge category encoding, not nav/selection) — revisit on v3 graph palette pass.
const NODE_COLORS: Record<GraphNode['type'], string> = {
  individual: baseColors.primary[500],
  entity: baseColors.secondary[500],
  case: baseColors.info[500],
};

const EDGE_COLORS: Record<GraphEdge['type'], string> = {
  ownership: baseColors.primary[300],
  borrower: baseColors.info[300],
  guarantor: baseColors.warning[300],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RelationshipGraph({
  nodes,
  edges,
  onNodeClick,
  width = 700,
  height = 500,
  rootNodeId,
}: RelationshipGraphProps): ReactElement {
  const theme = useTheme();
  const [simNodes, setSimNodes] = useState<SimNode[]>([]);
  const [simEdges, setSimEdges] = useState<SimEdge[]>([]);
  // biome-ignore lint/suspicious/noExplicitAny: d3-force simulation type is complex
  const simulationRef = useRef<any>(null);

  // Stabilise the callback reference so simulation tick doesn't re-mount
  const updatePositions = useCallback((sn: SimNode[], se: SimEdge[]) => {
    setSimNodes([...sn]);
    setSimEdges([...se]);
  }, []);

  useEffect(() => {
    if (nodes.length === 0) return;

    const simNodesCopy: SimNode[] = nodes.map((n) => ({
      ...n,
      x: width / 2 + (Math.random() - 0.5) * 100,
      y: height / 2 + (Math.random() - 0.5) * 100,
    }));

    const nodeMap = new Map(simNodesCopy.map((n) => [n.id, n]));

    const simEdgesCopy: SimEdge[] = edges
      .map((e) => {
        const src = nodeMap.get(e.source);
        const tgt = nodeMap.get(e.target);
        if (!src || !tgt) return null;
        return {
          source: src,
          target: tgt,
          label: e.label,
          edgeType: e.type,
        } satisfies SimEdge;
      })
      .filter((e): e is SimEdge => e !== null);

    const simulation = forceSimulation<SimNode>(simNodesCopy)
      .force(
        'link',
        forceLink<SimNode, SimEdge>(simEdgesCopy)
          .id((d) => d.id)
          .distance(120),
      )
      .force('charge', forceManyBody().strength(-300))
      .force('center', forceCenter(width / 2, height / 2))
      .force('collision', forceCollide<SimNode>().radius(40));

    simulation.on('tick', () => {
      updatePositions(simNodesCopy, simEdgesCopy);
    });

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
    };
  }, [nodes, edges, width, height, updatePositions]);

  // ------------------------------------------------------------------
  // Render helpers
  // ------------------------------------------------------------------

  const renderNode = (node: SimNode) => {
    const isRoot = node.id === rootNodeId;
    const size = isRoot ? 24 : 18;
    const strokeWidth = isRoot ? 3 : 1.5;
    const fill = NODE_COLORS[node.type] ?? baseColors.neutral[400];
    const truncatedLabel = node.label.length > 20 ? `${node.label.slice(0, 18)}...` : node.label;
    const isClickable = Boolean(node.href && onNodeClick);

    const interactiveProps = isClickable
      ? {
          role: 'button' as const,
          tabIndex: 0,
          style: { cursor: 'pointer' as const },
          onClick: () => onNodeClick?.(node),
          onKeyDown: (e: ReactKeyboardEvent<SVGGElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onNodeClick?.(node);
            }
          },
        }
      : {};

    return (
      <g key={node.id} transform={`translate(${node.x},${node.y})`} {...interactiveProps}>
        {node.type === 'case' ? (
          <rect
            x={-size / 2}
            y={-size / 2}
            width={size}
            height={size}
            rx={4}
            fill={fill}
            fillOpacity={0.15}
            stroke={fill}
            strokeWidth={strokeWidth}
            transform="rotate(45)"
          />
        ) : (
          <circle r={size} fill={fill} fillOpacity={0.15} stroke={fill} strokeWidth={strokeWidth} />
        )}
        <text
          y={size + 14}
          textAnchor="middle"
          fontSize={11}
          fill={theme.palette.text.primary}
          fontFamily={theme.typography.fontFamily as string}
        >
          {truncatedLabel}
        </text>
      </g>
    );
  };

  // ------------------------------------------------------------------
  // Empty state
  // ------------------------------------------------------------------

  if (nodes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          No relationship data available
        </Typography>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Relationship graph">
        {/* Edges */}
        {simEdges.map((edge, i) => {
          const edgeColor = EDGE_COLORS[edge.edgeType] ?? baseColors.neutral[300];
          const midX = (edge.source.x + edge.target.x) / 2;
          const midY = (edge.source.y + edge.target.y) / 2;

          return (
            <g key={`edge-${edge.source.id}-${edge.target.id}-${i}`}>
              <line
                x1={edge.source.x}
                y1={edge.source.y}
                x2={edge.target.x}
                y2={edge.target.y}
                stroke={edgeColor}
                strokeWidth={1.5}
                strokeOpacity={0.6}
              />
              <text
                x={midX}
                y={midY - 6}
                textAnchor="middle"
                fontSize={9}
                fill={theme.palette.text.secondary}
                fontFamily={theme.typography.fontFamily as string}
              >
                {edge.label}
              </text>
            </g>
          );
        })}

        {/* Nodes (rendered after edges so they appear on top) */}
        {simNodes.map(renderNode)}
      </svg>
    </Box>
  );
}
